import { PoweredBy } from "@/components/PoweredBy";
import { StatusBadge } from "@/components/StatusBadge";
import { StatusHistorySegments } from "@/components/StatusHistorySegments";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn, formatRelativeTime } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";

interface ServiceStatus {
	id: string;
	name: string;
	lastStatus: string | null;
	lastCheckedAt: string | null;
	history: { date: string; downtime: number | null; availability: number | null }[];
	incidents?: {
		startedAt: string;
		endedAt: string | null;
		durationMinutes: number | null;
		message: string | null;
		status: string | null;
	}[];
}

interface Payload {
	application: { id: string; name: string | null };
	services: ServiceStatus[];
	days: number;
}

async function fetchAppStatus(appId: string): Promise<Payload> {
	const res = await fetch(`/api/applications/${appId}/status?days=90`, {
		credentials: "include",
	});
	if (res.status === 404) throw new Error("Not found");
	if (res.status === 403) throw new Error("Not public");
	if (!res.ok) throw new Error("Failed to load");
	return res.json() as Promise<Payload>;
}

export function PublicAppStatus() {
	const { appId } = useParams<{ appId: string }>();

	const { data, isPending, error } = useQuery({
		queryKey: ["public-app-status", appId],
		queryFn: () => fetchAppStatus(appId ?? ""),
		enabled: !!appId,
	});

	if (error)
		return (
			<div className="container max-w-3xl mx-auto p-6 text-center text-destructive">
				{error.message}
			</div>
		);
	if (isPending || !data) {
		return (
			<div className="container max-w-3xl mx-auto p-6">
				<Skeleton className="h-8 w-48 mb-6" />
				<Skeleton className="h-14 w-full mb-6" />
				<ul className="space-y-4">
					{[1, 2, 3].map((i) => (
						<li key={i}>
							<Skeleton className="h-24 w-full" />
						</li>
					))}
				</ul>
			</div>
		);
	}

	const allUp = data.services.every((s) => s.lastStatus === "up");
	const anyDown = data.services.some((s) => s.lastStatus === "down");
	const latestCheck = data.services
		.map((s) => s.lastCheckedAt)
		.filter(Boolean)
		.sort()
		.pop();

	return (
		<div className="container max-w-3xl mx-auto p-6">
			<div className="mb-6">
				<h1 className="text-2xl font-bold tracking-tight text-balance">
					{data.application.name ?? "Status"}
				</h1>
			</div>
			<div
				className={cn(
					"mb-6 px-4 py-3 border font-medium text-center",
					anyDown && "bg-destructive/15 text-destructive border-destructive",
					allUp &&
						!anyDown &&
						"bg-emerald-500/15 text-emerald-400 border-emerald-500/50",
					!allUp && !anyDown && "bg-muted text-muted-foreground border-border",
				)}
			>
				{allUp
					? "All systems operational"
					: anyDown
						? "Some services are down"
						: "No recent data"}
				{latestCheck && (
					<span className="block text-xs font-normal mt-1 opacity-90 tabular-nums">
						Updated {formatRelativeTime(latestCheck)}
					</span>
				)}
			</div>
			<ul className="space-y-4">
				{data.services.map((s) => (
					<li key={s.id}>
						<Card className="overflow-hidden">
							<CardHeader className="py-3">
								<CardTitle className="text-lg flex flex-wrap justify-between items-center gap-2">
									<Link
										to={`/public/${appId}/${s.id}`}
										className="hover:underline focus:outline-none focus:ring-2 focus:ring-ring rounded"
									>
										{s.name}
									</Link>
									<StatusBadge status={s.lastStatus} />
								</CardTitle>
								{s.lastCheckedAt && (
									<p className="text-muted-foreground text-xs">
										Updated {formatRelativeTime(s.lastCheckedAt)}
									</p>
								)}
							</CardHeader>
							<CardContent className="pt-0 pb-4">
								<StatusHistorySegments
									history={s.history}
									incidents={s.incidents}
								/>
							</CardContent>
						</Card>
					</li>
				))}
			</ul>
			<div className="mt-6 text-center">
				<PoweredBy />
			</div>
		</div>
	);
}
